import type { ReactNode } from 'react';
import { memo, Suspense } from 'react';
import { Loader } from '@mantine/core';
import { LazyTiptapEditor } from '@/components/notes/editor';
import type { RelatedTaskSummary } from '@/types';
import type { Note } from '@/types/notes';
import { NoteMetadataSection } from './NoteMetadataSection';
import { NoteHistory } from './NoteHistory';
import styles from '../NoteDetailPanel.module.css';

interface NoteDetailContentViewProps {
  note: Note;
  activeTab: string;
  showMetadataSidebar: boolean;
  firstLinkedTask: RelatedTaskSummary | undefined;
  /** Omit to render the linked task as plain text (public, non-navigable). */
  onTaskNavigate?: (taskId: string, title: string, shortId?: string) => void;
  attachmentsContent: ReactNode;
}

export const NoteDetailContentView = memo(function NoteDetailContentView({
  note,
  activeTab,
  showMetadataSidebar,
  firstLinkedTask,
  onTaskNavigate,
  attachmentsContent,
}: NoteDetailContentViewProps) {
  if (activeTab !== 'content') {
    return <NoteHistory noteId={note.id} />;
  }

  return (
    <div className={styles.contentArea}>
      {!showMetadataSidebar && (
        <NoteMetadataSection
          note={note}
          linkedTask={firstLinkedTask}
          onTaskNavigate={onTaskNavigate}
        />
      )}
      <Suspense fallback={<Loader />}>
        <LazyTiptapEditor
          content={note.content}
          editable={false}
          variant={showMetadataSidebar ? 'expanded' : undefined}
        />
      </Suspense>

      {!showMetadataSidebar && attachmentsContent && (
        <div className={styles.metadataSection}>{attachmentsContent}</div>
      )}
    </div>
  );
});
